import { Events } from '@paperbits/common/events';
import * as ko from 'knockout';

import type { MapLocationModel } from '../mapModel';
import type { MapRuntimeConfig } from './runtime/mapRuntimeConfig';
import popupOverlay from './mapPopupOverlay.html';

const REPLY_DARK = [
    {
      stylers: [
        {
          color: '#31333e'
        }
      ]
    },
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#151f27'
        }
      ]
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#adadad'
        }
      ]
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#0f1626'
        }
      ]
    },
    {
      featureType: 'administrative.country',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#181c20'
        }
      ]
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#202428'
        }
      ]
    },
    {
      featureType: 'landscape',
      stylers: [
        {
          color: '#31333e'
        }
      ]
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry',
      stylers: [
        {
          color: '#28333e'
        }
      ]
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry',
      stylers: [
        {
          color: '#28333e'
        }
      ]
    },
    {
      featureType: 'landscape.natural.landcover',
      elementType: 'geometry',
      stylers: [
        {
          color: '#28333e'
        }
      ]
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry',
      stylers: [
        {
          color: '#28333e'
        }
      ]
    },
    {
        featureType: 'poi',
        stylers: [
            {
                visibility: 'off'
            }
        ]
    },
    /*{
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#485265'
        }
      ]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#a3a4a8'
        }
      ]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#0f1626'
        }
      ]
    },
    {
      featureType: 'poi.business',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#151f27'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#2fbc60'
        }
      ]
    },*/
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#3f465a'
        }
      ]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#98a5be'
        }
      ]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#1d2c4d'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1c2231'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#b0d5ce'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#023e58'
        }
      ]
    },
    {
      featureType: 'road.local',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#949494'
        }
      ]
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#0f1626'
        }
      ]
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#1f262d'
        }
      ]
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#1f262d'
        }
      ]
    },
    {
      featureType: 'water',
      stylers: [
        {
          color: '#151f27'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#0e1626'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3f4d5a'
        }
      ]
    }
  ];

const MARKER_WIDTH = 40;
const MARKER_HEIGHT = 48;

type Marker = google.maps.Marker;

export class GoogleMapsBindingHandler {
    private readonly selectedPin: ko.Observable<Marker>;

    private PIN: string;
    private PIN_SELECTED: string;
    private PIN_EMPTY: string;

    constructor() {
        const attach = this.attach.bind(this);

        ko.bindingHandlers['googlemap'] = {
            init(element: Element, valueAccessor: () => MapRuntimeConfig): void {
                attach(element, ko.toJS(valueAccessor()));
            }
        };
        this.selectedPin = ko.observable<Marker>();
    }

    private locationToPosition = async (location: string): Promise<google.maps.LatLng> => {
        const geocoder = new google.maps.Geocoder();
        const request: google.maps.GeocoderRequest = {};
        const coordinates = /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(location);

        if (coordinates) {
          return new Promise<google.maps.LatLng>((resolve) =>
            resolve(
              new google.maps.LatLng({
                lat: <any>coordinates[1] * 1,
                lng: <any>coordinates[2] * 1
              })
            )
          );
        } else {
            request.address = location;
        }

        return new Promise<google.maps.LatLng>((resolve, reject) => {
            geocoder.geocode(request, (results: google.maps.GeocoderResult[], status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                    resolve(results[0].geometry.location);
                }

                reject(`Could not geocode specified location: "${location}".`);
            });
        });
    };

    private computeCenter(locations: google.maps.LatLng[]): google.maps.LatLng {
        const bound = new google.maps.LatLngBounds();
        for (const loc of locations) {
            bound.extend(loc);
        }
        return bound.getCenter();
    }

    private async attach(element: HTMLElement, configuration: MapRuntimeConfig): Promise<void> {
        const googleMapLoad = await import('./bindingHandlers.googlemap-load');
        const com = configuration.corporate;
        this.PIN = com ? googleMapLoad.replyPin : googleMapLoad.minisitePin;
        this.PIN_SELECTED = com ? googleMapLoad.replyPinSelected : googleMapLoad.minisitePinSelected;
        this.PIN_EMPTY = com ? googleMapLoad.replyPinEmpty : googleMapLoad.minisitePinEmpty;
        const { gmapsLoader, gmapsMarkerClusterer, gmapsDefaultRenderer } = googleMapLoad;

        const loader = new gmapsLoader({
            apiKey: configuration.apiKey
        });
        await loader.load();
        // const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const zoom = configuration.zoom 
            ? typeof configuration.zoom === 'string' ? Number.parseInt(configuration.zoom) : 17
            : 17;
        const positionsWithLatitude: {location: MapLocationModel, position: google.maps.LatLng}[] = 
            configuration.locations
                ? await Promise.all(configuration.locations.map(async location => ({
                    location,
                    position: await this.locationToPosition(location.location)
                })))
                : [];
        const positions: google.maps.LatLng[] = positionsWithLatitude.map(p => p.position);
        const center = this.computeCenter(positions);
        const styleConfig: google.maps.MapOptions = configuration.mapType === 'terrain' ? {
            styles: REPLY_DARK
        } : {
            mapTypeId: configuration.mapType
        };
        const map = new google.maps.Map(element, {
            // mapId: 'reply-map-0', // TODO unique
            streetViewControl: false,
            mapTypeControl: false,
            draggable: true, // TODO
            zoomControl: true,
            scaleControl: true,
            zoom,
            center,
            ...styleConfig
        });
        
        class PopupAnchor extends google.maps.OverlayView {
            private readonly content: HTMLElement;

            constructor(
                private readonly position: google.maps.LatLng,
                private readonly popupKey: string
            ) {
                super();
                this.content = document.createElement('div');
            }

            /** Called when the popup is added to the map. */
            public onAdd(): void {
                PopupAnchor.preventMapHitsAndGesturesFrom(this.content);

                this.content.setAttribute('data-toggle', 'popup');
                this.content.setAttribute('data-target', `#${this.popupKey.replace('popups/', 'popups')}`);
                this.content.setAttribute('data-position', 'top');
                this.content.classList.add('zero-height');

                this.getPanes().floatPane.appendChild(this.content);
                // document.dispatchEvent(new CustomEvent("onPopupRequested", { detail: this.popupKey }));
            }

            /** Called each frame when the popup needs to draw itself. */
            public draw(): void {
                const elementPosition = this.getProjection().fromLatLngToDivPixel(this.position);

                this.content.style.left = `${elementPosition.x - Math.floor(MARKER_WIDTH / 2)}px`;
                this.content.style.top = `${elementPosition.y - Math.floor(MARKER_HEIGHT / 2)}px`;
                this.content.style.width = `${MARKER_WIDTH}px`;
                this.content.style.height = `${MARKER_HEIGHT}px`;

                document.dispatchEvent(new CustomEvent('onPopupRepositionRequested', {
                    detail: {
                        element: this.content,
                        placement: 'top'
                        // offsetX:
                        // offsetY:
                    }
                }));
            }
        }

        const markers: Marker[] = [];

      function showReplyPopup(popupContent: string, component: GoogleMapsBindingHandler) {
        const officesPopup = document.getElementById('reply-offices-popup');
        if (configuration.showReplyOffices && configuration.usePopupOverlay && !officesPopup) {
          const replyOfficesPopup = document.createElement('div');
          replyOfficesPopup.innerHTML = popupOverlay;
          replyOfficesPopup.querySelector('.close-anchor')?.addEventListener('click', () => {
            replyOfficesPopup.remove();
            component.selectedPin()?.setIcon({
              url: component.PIN,
              scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
            });
            component.selectedPin(undefined);
          });
          element.querySelector('div').appendChild(replyOfficesPopup);
        } else {
          officesPopup.classList.remove('hidden');
        }
        document.getElementById('reply-offices-popup-content').innerHTML = popupContent;
      }

      for (const {position, location} of positionsWithLatitude) {
            const marker: Marker = new google.maps.Marker();
            markers.push(marker);
            marker.setMap(map);
            if (configuration.showReplyOffices) {
              marker.setIcon({
                url: this.PIN,
                scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
              });
            } else {
              if (configuration.markerIcon) {
                const icon: google.maps.Icon = {
                  url: configuration.markerIcon,
                  scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
                };
                marker.setIcon(icon);
              }
            }
            marker.setPosition(position);
            if (location.popupKey) {
                const anchor = new PopupAnchor(position, location.popupKey);
                anchor.setMap(map);
                marker.addListener(Events.Click, () => document.dispatchEvent(new CustomEvent('onPopupRequested', { detail: location.popupKey })));
            } else {
                const infoWindow = new google.maps.InfoWindow();
                infoWindow.setContent(location.caption);
                infoWindow.close();
                marker.addListener(Events.Click, () => {
                  if (location.caption?.length > 0) {
                    if (configuration.showReplyOffices && configuration.usePopupOverlay) {
                      this.selectedPin()?.setIcon({
                        url: this.PIN,
                        scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
                      });
                      this.selectedPin(marker);
                      marker.setIcon({
                        url: this.PIN_SELECTED,
                        scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
                      });
                      showReplyPopup(location.caption, this);
                    } else {
                      infoWindow.open(map, marker);
                    }
                  }
                });
            }
        }

        const renderer = !configuration?.showReplyOffices ? new gmapsDefaultRenderer() : {
          render: ({ count, position }) =>
            new google.maps.Marker({
              icon: {
                url: this.PIN_EMPTY,
                scaledSize: new google.maps.Size(MARKER_WIDTH, MARKER_HEIGHT),
              },
              label: { text: String(count), color: '#151F27', fontSize: '14px' },
              position,
              // adjust zIndex to be above other markers
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            }),
        };

        new gmapsMarkerClusterer({ markers, map, renderer });
    }
}
